import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AdminNavBar from './AdminNavBar';

const DEFAULT_SIZES = {
  '3:4': { width: 150, height: 200 },
  '4:3': { width: 200, height: 150 },
  '1:1': { width: 175, height: 175 },
};

const Toolbar = ({ selected, onSelect, containerRef }) => {
  return (
    <div ref={containerRef} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <label style={{ fontWeight: 600 }}>Rasio Slot:</label>
      <select 
        value={selected || ''} 
        onChange={(e) => onSelect(e.target.value || null)}
        style={{ 
          padding: '6px 8px', 
          borderRadius: 6, 
          border: '1px solid #ced4da',
          background: '#fff',
          cursor: 'pointer',
          minWidth: '80px'
        }}
      >
        <option value="">Pilih Rasio</option>
        <option value="3:4">3:4 (Portrait)</option>
        <option value="4:3">4:3 (Landscape)</option>
        <option value="1:1">1:1 (Square)</option>
      </select>
    </div>
  );
};

const SHAPE_COLORS = [
  '#8EB080', // 1
  '#F2B785', // 2
  '#FF5252', // 3
  '#B4E0DD', // 4
  '#CEB4E0', // 5
  '#FCF99F', // 6
  '#3A3A4F', // 7
  '#992955', // 8
];

function getShapeColor(indexZeroBased) {
  const i = indexZeroBased;
  if (i < SHAPE_COLORS.length) return SHAPE_COLORS[i];
  return '#696969';
}

const Workspace = () => {
  // State untuk copy-paste slot shape (clipboard virtual)
  const [clipboardSlot, setClipboardSlot] = useState(null);
  const shapesRef = useRef([]);
  const [shapeRatio, setShapeRatio] = useState('3:4');
  const [shapes, setShapes] = useState([]);
  // Sinkronisasi ke ref supaya selalu fresh di handler keyboard
  useEffect(() => { shapesRef.current = shapes; }, [shapes]);
  const [dragState, setDragState] = useState(null);
  const [rotateState, setRotateState] = useState(null);
  const [resizeState, setResizeState] = useState(null);
  // Multi-select mode
  const [selectedIds, setSelectedIds] = useState([]);
  const toolbarRef = useRef(null);
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [bgNatural, setBgNatural] = useState(null); // { w, h }
  const prevBgUrlRef = useRef(null);
  const workspaceRef = useRef(null);
  const idCounterRef = useRef(0);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [workspaceName, setWorkspaceName] = useState('My Workspace');
  const [workspaceCategory, setWorkspaceCategory] = useState('');
  const [backgroundPath, setBackgroundPath] = useState(null); // server path like /uploads/xxx
  const [isSaving, setIsSaving] = useState(false);
  const [workspacesList, setWorkspacesList] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedWorkspaceToLoad, setSelectedWorkspaceToLoad] = useState('');

  const API_BASE = 'https://api.laviopaperie.com';

  // Cari slot number berikutnya yang unik untuk ratio yang dipilih
  const findNextSlotNum = useCallback(() => {
    // Kumpulkan nomor slot dari SEMUA slot apapun ratio
    const allShapes = shapes.filter((s) => s.type === 'shape');
    const usedSlotNums = new Set();
    let num = 0;
    allShapes.forEach((s) => {
      if (s.sotNumFixed) usedSlotNums.add(s.slotNumFixed);
      else {
        num += 1;
        usedSlotNums.add(num);
      }
    });
    // Cari slot terkecil yang belum dipakai
    let candidate = 1;
    while (usedSlotNums.has(candidate)) {
      candidate += 1;
    }
    return candidate;
  }, [shapes]);

  const addShapeAt = useCallback((clientX, clientY) => {
    const rect = workspaceRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Cari size slot terakhir (ratio sama), kalau ada shape lain
    const existing = shapes.filter((s) => s.type === 'shape' && s.ratio === shapeRatio);
    let width, height;
    if (existing.length > 0) {
      const last = existing[existing.length - 1];
      width = last.width;
      height = last.height;
    } else {
      ({ width, height } = DEFAULT_SIZES[shapeRatio]);
    }
    const x = clientX - rect.left - width / 2;
    const y = clientY - rect.top - height / 2;
    idCounterRef.current += 1;
    // Dapatkan slot number unik berikutnya
    const slotNum = findNextSlotNum();
    const newShape = {
      type: 'shape',
      id: `shape_${Date.now()}_${idCounterRef.current}`,
      ratio: shapeRatio,
      x,
      y,
      width,
      height,
      rotationDeg: 0,
      borderRadius: 8,
      slotNumFixed: slotNum,
    };
    setShapes((prev) => [...prev, newShape]);
  }, [shapeRatio, shapes, findNextSlotNum]);

  const onWorkspaceClick = useCallback((e) => {
    // Only handle when clicking empty area (not when dragging/rotating/resizing)
    if (dragState || rotateState || resizeState) return;
    // Always clear selection when clicking empty workspace
    setSelectedIds([]);
    // If a ratio is selected, also add a new shape at click position
    if (shapeRatio) {
      addShapeAt(e.clientX, e.clientY);
    }
  }, [addShapeAt, dragState, rotateState, resizeState, shapeRatio]);

  const onStartDrag = useCallback((e, shapeId) => {
    e.stopPropagation();
    const rect = workspaceRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Untuk multi-select, drag semua yang terpilih!
    const nowSelected = selectedIds.includes(shapeId) ? selectedIds : [shapeId];
    const originShapes = shapes.filter(s => nowSelected.includes(s.id));
    // Simpan posisi awal semua yang dipilih & posisi mouse
    setDragState({
      dragIds: nowSelected,
      mouseStartX: e.clientX - rect.left,
      mouseStartY: e.clientY - rect.top,
      origins: originShapes.map(s => ({ id: s.id, x: s.x, y: s.y })),
      baseYMap: Object.fromEntries(originShapes.map(s => [s.id, s.y]))
    });
  }, [shapes, selectedIds]);

  const onStartRotate = useCallback((e, shapeId) => {
    e.stopPropagation();
    const rect = workspaceRef.current?.getBoundingClientRect();
    if (!rect) return;
    const target = shapes.find((s) => s.id === shapeId);
    if (!target) return;
    const centerX = target.x + target.width / 2;
    const centerY = target.y + target.height / 2;
    const pointerX = e.clientX - rect.left;
    const pointerY = e.clientY - rect.top;
    const startAngle = Math.atan2(pointerY - centerY, pointerX - centerX);
    setSelectedIds([shapeId]);
    setRotateState({ shapeId, centerX, centerY, startAngle, startRotation: target.rotationDeg });
  }, [shapes]);

  const onDeleteShape = useCallback((shapeId) => {
    setShapes((prev) => {
      const target = prev.find((s) => s.id === shapeId);
      if (target && target.type === 'asset' && target.src) {
        try { URL.revokeObjectURL(target.src); } catch {}
      }
      return prev.filter((s) => s.id !== shapeId);
    });
  }, []);

  const onAdjustRadius = useCallback((shapeId, delta) => {
    setShapes((prev) => prev.map((s) => {
      if (s.id !== shapeId) return s;
      const maxR = Math.min(s.width, s.height) / 2;
      const next = Math.max(0, Math.min(maxR, (s.borderRadius || 0) + delta));
      return { ...s, borderRadius: next };
    }));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!workspaceRef.current) return;
      const rect = workspaceRef.current.getBoundingClientRect();
      if (dragState && dragState.dragIds) {
        const { dragIds, mouseStartX, mouseStartY, origins, baseYMap } = dragState;
        const curX = e.clientX - rect.left;
        const curY = e.clientY - rect.top;
        const dx = curX - mouseStartX;
        const dy = curY - mouseStartY;
        setShapes((prev) => prev.map(s => {
          const idx = dragIds.indexOf(s.id);
          if (idx !== -1) {
            // Shift → lock only vertical
            let nx = origins[idx].x + dx;
            let ny = origins[idx].y + (e.shiftKey ? 0 : dy);
            // Optionally clamp to background bounds here
            return { ...s, x: nx, y: ny };
          }
          return s;
        }));
      } else if (rotateState) {
        const { shapeId, centerX, centerY, startAngle, startRotation } = rotateState;
        const pointerX = e.clientX - rect.left;
        const pointerY = e.clientY - rect.top;
        const angle = Math.atan2(pointerY - centerY, pointerX - centerX);
        const deltaDeg = (angle - startAngle) * (180 / Math.PI);
        const rotationDeg = startRotation + deltaDeg;
        setShapes((prev) => prev.map((s) => s.id === shapeId ? { ...s, rotationDeg } : s));
      } else if (resizeState) {
        const { shapeId, handle, startMouseX, startMouseY, startRect, rotationRad } = resizeState;
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const deltaGlobalX = mouseX - startMouseX;
        const deltaGlobalY = mouseY - startMouseY;
        const cosInv = Math.cos(-rotationRad);
        const sinInv = Math.sin(-rotationRad);
        const deltaLocalX = deltaGlobalX * cosInv - deltaGlobalY * sinInv;
        const deltaLocalY = deltaGlobalX * sinInv + deltaGlobalY * cosInv;

        const minWidth = 40;
        const minHeight = 40;
        const aspect = startRect.width / Math.max(1, startRect.height);

        let sX = 1, sY = 1;
        if (handle === 'ne') { sX = 1; sY = -1; }
        if (handle === 'sw') { sX = -1; sY = 1; }
        if (handle === 'nw') { sX = -1; sY = -1; }

        const effDX = sX * deltaLocalX;
        const effDY = sY * deltaLocalY;

        let candW_A = Math.max(minWidth, startRect.width + effDX);
        let candH_A = Math.max(minHeight, candW_A / aspect);
        if (candH_A < minHeight) {
          candH_A = minHeight;
          candW_A = candH_A * aspect;
        }

        let candH_B = Math.max(minHeight, startRect.height + effDY);
        let candW_B = Math.max(minWidth, candH_B * aspect);
        if (candW_B < minWidth) {
          candW_B = minWidth;
          candH_B = candW_B / aspect;
        }

        const pxEff = startRect.width + effDX;
        const pyEff = startRect.height + effDY;
        const dA = (pxEff - candW_A) * (pxEff - candW_A) + (pyEff - candH_A) * (pyEff - candH_A);
        const dB = (pxEff - candW_B) * (pxEff - candW_B) + (pyEff - candH_B) * (pyEff - candH_B);

        let newW_eff, newH_eff;
        if (dA <= dB) { newW_eff = candW_A; newH_eff = candH_A; } else { newW_eff = candW_B; newH_eff = candH_B; }

        let newWidth = newW_eff;
        let newHeight = newH_eff;
        let newX = startRect.x;
        let newY = startRect.y;

        if (handle === 'ne') {
          const useDY = startRect.height - newHeight;
          const cosR = Math.cos(rotationRad);
          const sinR = Math.sin(rotationRad);
          const worldTopDX = -useDY * sinR;
          const worldTopDY = useDY * cosR;
          newX = startRect.x + worldTopDX;
          newY = startRect.y + worldTopDY;
        } else if (handle === 'sw') {
          const useDX = startRect.width - newWidth;
          const cosR = Math.cos(rotationRad);
          const sinR = Math.sin(rotationRad);
          const worldLeftDX = useDX * cosR;
          const worldLeftDY = useDX * sinR;
          newX = startRect.x + worldLeftDX;
          newY = startRect.y + worldLeftDY;
        } else if (handle === 'nw') {
          const useDX = startRect.width - newWidth;
          const useDY = startRect.height - newHeight;
          const cosR = Math.cos(rotationRad);
          const sinR = Math.sin(rotationRad);
          const worldDX = useDX * cosR - useDY * sinR;
          const worldDY = useDX * sinR + useDY * cosR;
          newX = startRect.x + worldDX;
          newY = startRect.y + worldDY;
        }

        setShapes((prev) => prev.map((s) => s.id === shapeId ? { ...s, x: newX, y: newY, width: newWidth, height: newHeight } : s));
      }
    };

    const onUp = () => {
      if (dragState) setDragState(null);
      if (rotateState) setRotateState(null);
      if (resizeState) setResizeState(null);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mouseleave', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mouseleave', onUp);
    };
  }, [dragState, rotateState, resizeState]);

  const workspaceStyle = useMemo(() => ({
    position: 'relative',
    width: '100%',
    height: 'calc(100vh - 80px)',
    background: '#f8f9fa',
    border: '1px dashed #ced4da',
    borderRadius: 12,
    overflow: 'hidden',
    cursor: 'crosshair',
  }), []);

  // Deselect ratio when clicking outside toolbar and workspace
  useEffect(() => {
    const onDocMouseDown = (e) => {
      const wk = workspaceRef.current;
      const tb = toolbarRef.current;
      if (wk && wk.contains(e.target)) return;
      if (tb && tb.contains(e.target)) return;
      setShapeRatio(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  // Keyboard controls for selected shape: Delete, Arrow, Copy/Paste slot shape
  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = e.target && e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;
      // Copy slot shape (Ctrl+C)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (!selectedIds.length) return;
        const curShapes = shapesRef.current;
        // Copy hanya slot terakhir yang dipilih (untuk share slot)
        const selId = selectedIds[selectedIds.length - 1];
        const selectedShape = curShapes.find((s) => s.id === selId && s.type === 'shape');
        if (selectedShape) {
          // Cari slot number dari shape
          let slotNum = null;
          // Jika slotNumFixed, pakai itu
          if (selectedShape.slotNumFixed) {
            slotNum = selectedShape.slotNumFixed;
          } else {
            // Cari index urut di shapes untuk ratio yang sama (dan id ini)
            const ratioShapes = curShapes.filter((s) => s.type === 'shape' && s.ratio === selectedShape.ratio);
            const idxInRatio = ratioShapes.findIndex((s) => s.id === selectedShape.id);
            slotNum = idxInRatio + 1;
          }
          // Simpan juga posisi x, y (plus opsional: ukuran, rotasi, dst)
          setClipboardSlot({ 
            ratio: selectedShape.ratio, 
            slotNum,
            x: selectedShape.x,
            y: selectedShape.y,
            width: selectedShape.width,
            height: selectedShape.height,
            rotationDeg: selectedShape.rotationDeg,
            borderRadius: selectedShape.borderRadius
          });
        }
        return;
      }
      // Paste slot shape (Ctrl+V)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (!clipboardSlot) return;
        idCounterRef.current += 1;
        const newId = `shape_${Date.now()}_${idCounterRef.current}`;
        // Cari nomor slot urut berikutnya untuk ratio clipboardSlot
        const slotNum = findNextSlotNum();
        setShapes((prev) => [
          ...prev, 
          {
            type: 'shape',
            id: newId,
            ratio: clipboardSlot.ratio,
            x: clipboardSlot.x,
            y: clipboardSlot.y,
            width: clipboardSlot.width,
            height: clipboardSlot.height,
            rotationDeg: clipboardSlot.rotationDeg || 0,
            borderRadius: clipboardSlot.borderRadius || 8,
            slotNumFixed: slotNum
          }
        ]);
        setSelectedIds([newId]);
        return;
      }
      // Jangan lanjut jika delete/arrow tanpa selected apapun
      if (!selectedIds.length) return;
      // Delete
      if (e.key === 'Delete') {
        e.preventDefault();
        setShapes((prev) => prev.filter((s) => !selectedIds.includes(s.id)));
        setSelectedIds([]);
        return;
      }
      // Arrow movement (geser semua selected)
      const step = e.shiftKey ? 10 : 1;
      let dx = 0, dy = 0;
      if (e.key === 'ArrowLeft') dx = -step;
      else if (e.key === 'ArrowRight') dx = step;
      else if (e.key === 'ArrowUp') dy = -step;
      else if (e.key === 'ArrowDown') dy = step;
      else return;
      e.preventDefault();
      setShapes((prev) => prev.map((s) => selectedIds.includes(s.id) ? { ...s, x: s.x + dx, y: s.y + dy } : s));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [clipboardSlot, shapes, setShapes, onDeleteShape]);

  // Revoke old background object URL to avoid memory leaks
  useEffect(() => {
    if (prevBgUrlRef.current && prevBgUrlRef.current !== backgroundUrl) {
      URL.revokeObjectURL(prevBgUrlRef.current);
    }
    prevBgUrlRef.current = backgroundUrl;
    return () => {
      if (prevBgUrlRef.current) URL.revokeObjectURL(prevBgUrlRef.current);
    };
  }, [backgroundUrl]);

  const onPickBackground = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setBackgroundUrl(url);
    setBackgroundFile(file);
    // Load natural size to compute displayed width for clamping
    const img = new Image();
    img.onload = () => {
      setBgNatural({ w: img.naturalWidth || img.width, h: img.naturalHeight || img.height });
    };
    img.src = url;
    setBackgroundPath(null); // reset server path since changed
  }, []);

  const onClearBackground = useCallback(() => {
    setBackgroundUrl(null);
    setBgNatural(null);
    setBackgroundPath(null);
    setBackgroundFile(null);
  }, []);

  // Insert 2: Assets picker
  const onPickAssets = useCallback((e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (!files.length) return;
    const rect = workspaceRef.current?.getBoundingClientRect();
    const workspaceWidth = rect ? rect.width : 800;
    const workspaceHeight = rect ? rect.height : 600;
    const centerX = workspaceWidth / 2;
    const centerY = workspaceHeight / 3; // place a bit below top

    files.forEach((file, idx) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const naturalW = img.naturalWidth || img.width;
        const naturalH = img.naturalHeight || img.height;
        const aspect = naturalW / Math.max(1, naturalH);
        const baseHeight = 200; // default initial height
        const width = Math.max(40, Math.round(baseHeight * aspect));
        const height = Math.max(40, baseHeight);
        // Stagger slightly so multiple inserts are not perfectly overlapping
        const x = Math.round(centerX - width / 2 + (idx * 20));
        const y = Math.round(centerY - height / 2 + (idx * 20));
        idCounterRef.current += 1;
        const newAsset = {
          type: 'asset',
          id: `asset_${Date.now()}_${idCounterRef.current}`,
          src: url,
          layer: 'front',
          localFile: file,
          x,
          y,
          width,
          height,
          rotationDeg: 0,
          borderRadius: 8,
        };
        setShapes((prev) => [...prev, newAsset]);
      };
      img.onerror = () => {
        // Fallback: if cannot read size, still add with default
        const width = 200, height = 200;
        const x = Math.round(centerX - width / 2 + (idx * 20));
        const y = Math.round(centerY - height / 2 + (idx * 20));
        idCounterRef.current += 1;
        const newAsset = {
          type: 'asset',
          id: `asset_${Date.now()}_${idCounterRef.current}`,
          src: url,
          layer: 'front',
          localFile: file,
          x,
          y,
          width,
          height,
          rotationDeg: 0,
          borderRadius: 8,
        };
        setShapes((prev) => [...prev, newAsset]);
      };
      img.src = url;
    });
    // reset input value to allow re-selecting the same files
    e.target.value = '';
  }, []);

  // Cleanup all asset object URLs on unmount
  useEffect(() => {
    return () => {
      shapes.forEach((s) => {
        if (s.type === 'asset' && s.src) {
          try { URL.revokeObjectURL(s.src); } catch {}
        }
      });
    };
  }, [shapes]);

  // Helper to derive server path from full URL
  const deriveServerPath = (fullUrl) => {
    try {
      const u = new URL(fullUrl, window.location.origin);
      // If pointing to backend uploads
      const idx = u.pathname.indexOf('/uploads/');
      if (idx !== -1) return u.pathname.substring(idx);
    } catch (e) {
      // ignore
    }
    return null;
  };

  // Upload blobs (background/assets) to backend and return their server paths
  const uploadFiles = async (blobsWithNames) => {
    if (!blobsWithNames.length) return [];
    const fd = new FormData();
    blobsWithNames.forEach(({ blob, name }) => {
      const file = new File([blob], name || 'file.bin', { type: blob.type || 'application/octet-stream' });
      fd.append('files', file);
    });
    const res = await fetch(`${API_BASE}/api/upload-assets`, {
      method: 'POST',
      body: fd,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Gagal upload');
    // Map to server paths
    // data.files: [{ originalname, filename, size, mimetype }]
    return (data.files || []).map((f) => `/uploads/${f.filename}`);
  };

  const getBlobFromUrl = async (url) => {
    // Prefer fetch; if it fails (e.g., due to blob: referrer policy), fallback to XHR
    try {
      const r = await fetch(url);
      return await r.blob();
    } catch (e) {
      // Fallback via XHR
      try {
        const blob = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', url, true);
          xhr.responseType = 'blob';
          xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response);
            else reject(new Error('XHR blob fetch failed'));
          };
          xhr.onerror = () => reject(new Error('XHR error'));
          xhr.send();
        });
        return blob;
      } catch (e2) {
        throw e2;
      }
    }
  };

  const saveWorkspace = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      // Ensure background is on server if present
      let bgPath = backgroundPath;
      if (backgroundUrl && !bgPath) {
        const derived = deriveServerPath(backgroundUrl);
        if (derived) {
          bgPath = derived;
        } else {
          const blob = backgroundFile || await getBlobFromUrl(backgroundUrl);
          const [uploaded] = await uploadFiles([{ blob, name: backgroundFile?.name || 'background' }]);
          bgPath = uploaded;
        }
      }

      // Prepare asset uploads for any local blob URLs
      const assetsNeedingUpload = shapes.filter((s) => s.type === 'asset').filter((s) => !deriveServerPath(s.src));
      let uploadedAssetPaths = [];
      if (assetsNeedingUpload.length > 0) {
        const blobs = await Promise.all(assetsNeedingUpload.map(async (s, i) => ({ blob: s.localFile || await getBlobFromUrl(s.src), name: s.localFile?.name || `asset_${i}` })));
        uploadedAssetPaths = await uploadFiles(blobs);
      }

      // Assign uploaded paths back to shapes
      let uploadIdx = 0;
      const mappedShapes = shapes.map((s) => {
        if (s.type === 'asset') {
          const existingPath = deriveServerPath(s.src);
          if (existingPath) {
            return { ...s, srcPath: existingPath };
          }
          const p = uploadedAssetPaths[uploadIdx++];
          return { ...s, srcPath: p };
        }
        return s;
      });
      setShapes(mappedShapes);

      // Create or update workspace
      const canvasRect = workspaceRef.current?.getBoundingClientRect();
      const payloadWs = {
        name: workspaceName || 'Untitled',
        category: workspaceCategory || null,
        background_path: bgPath || null,
        bg_natural_width: bgNatural?.w || null,
        bg_natural_height: bgNatural?.h || null,
        canvas_width: canvasRect ? Math.round(canvasRect.width) : null,
        canvas_height: canvasRect ? Math.round(canvasRect.height) : null,
      };

      let currentId = workspaceId;
      if (!currentId) {
        const resCreate = await fetch(`${API_BASE}/api/workspaces`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payloadWs),
        });
        const dataCreate = await resCreate.json();
        if (!resCreate.ok) throw new Error(dataCreate.message || 'Gagal membuat workspace');
        currentId = dataCreate.id;
        setWorkspaceId(currentId);
      } else {
        const resUpdate = await fetch(`${API_BASE}/api/workspaces/${currentId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payloadWs),
        });
        const dataUpdate = await resUpdate.json();
        if (!resUpdate.ok) throw new Error(dataUpdate.message || 'Gagal update workspace');
      }
      setBackgroundPath(bgPath || null);

      // Save items
      const itemsPayload = mappedShapes.map((s, idx) => {
        if (s.type === 'asset') {
          return {
            item_type: 'asset',
            src_path: s.srcPath || deriveServerPath(s.src),
            ratio: null,
            x: Math.round(s.x), y: Math.round(s.y),
            width: Math.round(s.width), height: Math.round(s.height),
            rotation_deg: s.rotationDeg || 0,
            border_radius: 0,
            layer: s.layer || 'front',
            order_index: idx,
          };
        }
        return {
          item_type: 'shape',
          src_path: null,
          ratio: s.ratio || null,
          x: Math.round(s.x), y: Math.round(s.y),
          width: Math.round(s.width), height: Math.round(s.height),
          rotation_deg: s.rotationDeg || 0,
          border_radius: Math.round(s.borderRadius || 0),
          layer: s.layer || 'front',
          order_index: idx,
        };
      });

      const resItems = await fetch(`${API_BASE}/api/workspaces/${currentId}/items`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsPayload }),
      });
      const dataItems = await resItems.json();
      if (!resItems.ok) throw new Error(dataItems.message || 'Gagal menyimpan items');

      alert('Workspace tersimpan');
    } catch (err) {
      alert(err.message || 'Gagal menyimpan');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, backgroundPath, backgroundUrl, bgNatural, shapes, workspaceId, workspaceName]);

  const loadWorkspacesList = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/workspaces?limit=50`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Gagal load list');
      const list = data.workspaces || [];
      setWorkspacesList(list);
      const uniqCats = Array.from(new Set(list.map((w) => (w.category || '').trim()).filter(Boolean)));
      setCategories(uniqCats);
    } catch (e) {
      alert(e.message);
    }
  }, []);

  const loadWorkspaceById = useCallback(async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/workspaces/${id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Gagal load workspace');
      const { workspace, items } = data;
      setWorkspaceId(workspace.id);
      setWorkspaceName(workspace.name || 'Loaded Workspace');
      setWorkspaceCategory(workspace.category || '');
      setBackgroundPath(workspace.background_path || null);
      setBgNatural({ w: workspace.bg_natural_width || null, h: workspace.bg_natural_height || null });
      setBackgroundUrl(workspace.background_path ? `${API_BASE}${workspace.background_path}` : null);
      const restored = (items || []).map((it) => {
        if (it.item_type === 'asset') {
          return {
            type: 'asset',
            id: `asset_${it.id}`,
            src: it.src_path ? `${API_BASE}${it.src_path}` : '',
            srcPath: it.src_path || null,
            x: it.x, y: it.y,
            width: it.width, height: it.height,
            rotationDeg: it.rotation_deg || 0,
            borderRadius: 0,
            layer: it.layer || 'front',
          };
        }
        return {
          type: 'shape',
          id: `shape_${it.id}`,
          ratio: it.ratio || '3:4',
          x: it.x, y: it.y,
          width: it.width, height: it.height,
          rotationDeg: it.rotation_deg || 0,
          borderRadius: it.border_radius || 0,
          layer: it.layer || 'front',
        };
      });
      setShapes(restored);
      setSelectedIds([]);
      alert('Workspace dimuat');
    } catch (e) {
      alert(e.message);
    }
  }, []);

  return (
    <div style={{ padding: 16 }}>
      {/* <AdminNavBar active="/workspace" /> */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Workspace</h2>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          {/* Left controls: Ratio, Insert Background, Insert Assets */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ fontWeight: 600 }}>Nama</label>
              <input value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} style={{ padding: '6px 8px' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ fontWeight: 600 }}>Category</label>
              <input
                value={workspaceCategory}
                onChange={(e) => setWorkspaceCategory(e.target.value)}
                placeholder="e.g. Wedding, Birthday"
                style={{ padding: '6px 8px' }}
              />
            </div>
            <Toolbar selected={shapeRatio} onSelect={setShapeRatio} containerRef={toolbarRef} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ fontWeight: 600 }}>Insert 1: Background</label>
              <input type="file" accept="image/*" onChange={onPickBackground} />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ fontWeight: 600 }}>Insert 2: Assets</label>
              <input type="file" accept="image/*" multiple onChange={onPickAssets} />
            </div>
          </div>
          {/* Right controls: Delete Background, Layer controls */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button disabled={isSaving} className="start-button" onClick={saveWorkspace} style={{ padding: '6px 10px' }}>{isSaving ? 'Menyimpan...' : 'Save'}</button>
            <button className="start-button" onClick={loadWorkspacesList} style={{ padding: '6px 10px' }}>Load List</button>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ fontWeight: 600 }}>Filter Category</label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} style={{ padding: '6px 8px' }}>
                <option value="">All</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <select value={selectedWorkspaceToLoad} onChange={(e) => setSelectedWorkspaceToLoad(e.target.value)} style={{ padding: '6px 8px' }}>
              <option value="">Pilih Workspace...</option>
              {workspacesList
                .filter((w) => !categoryFilter || (w.category || '').trim() === categoryFilter)
                .map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.id} - {w.name}{w.category ? ` (${w.category})` : ''}
                  </option>
                ))}
            </select>
            <button className="start-button" disabled={!selectedWorkspaceToLoad} onClick={() => loadWorkspaceById(selectedWorkspaceToLoad)} style={{ padding: '6px 10px' }}>Load</button>
            {backgroundUrl && (
              <button className="start-button" onClick={onClearBackground} style={{ padding: '6px 10px' }}>Hapus Background</button>
            )}
            {selectedIds.length === 1 && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <label style={{ fontWeight: 600 }}>Layer</label>
                <button
                  className="start-button"
                  onClick={() => setShapes((prev) => prev.map((s) => selectedIds.length === 1 && s.id === selectedIds[0] ? { ...s, layer: 'front' } : s))}
                  style={{ padding: '6px 10px' }}
                >
                  Bring Front
                </button>
                <button
                  className="start-button"
                  onClick={() => setShapes((prev) => prev.map((s) => selectedIds.length === 1 && s.id === selectedIds[0] ? { ...s, layer: 'back' } : s))}
                  style={{ padding: '6px 10px' }}
                >
                  Send Back
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div ref={workspaceRef} style={workspaceStyle} onMouseDown={(e) => e.target === workspaceRef.current && onWorkspaceClick(e)}>
        {backgroundUrl && (
          <img
            src={backgroundUrl}
            alt="workspace background"
            style={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              height: '100%',
              width: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
              pointerEvents: 'none',
              userSelect: 'none',
              zIndex: 0,
            }}
          />
        )}
        {(() => {
          const shapeNumberById = {};
          let counter = 0;
          shapes.forEach((it) => {
            if (it.type === 'shape') {
              counter += 1;
              shapeNumberById[it.id] = counter;
            }
          });
          return shapes.map((s, idx) => {
            // Jika shape hasil paste (copy slot), pakai nomor slot yang fixed
            const shapeNum = s.type === 'shape' ? (s.slotNumFixed || shapeNumberById[s.id] || 0) : null;
            const backgroundColor = s.type === 'shape' ? getShapeColor(Math.max(0, (shapeNum || 1) - 1)) : 'transparent';
            return (
            <div
              key={s.id}
             onMouseDown={(e) => {
               if (e.ctrlKey || e.metaKey) {
                 setSelectedIds((prev) => prev.includes(s.id)
                   ? prev.filter((id) => id !== s.id)
                   : [...prev, s.id]
                 );
                 // Jangan mulai drag pada ctrl/meta click!
               } else {
                 setSelectedIds([s.id]);
                 onStartDrag(e, s.id);
               }
             }}
            style={{
              position: 'absolute',
              left: s.x,
              top: s.y,
              width: s.width,
              height: s.height,
              transform: `rotate(${s.rotationDeg}deg)`,
              transformOrigin: 'center center',
              background: backgroundColor,
              border: selectedIds.includes(s.id) ? '2.5px solid #0092ff' : '2px solid transparent',
              borderRadius: s.type === 'asset' ? 0 : (s.borderRadius ?? 8),
              boxShadow: s.type === 'asset' ? 'none' : '0 4px 12px rgba(13,110,253,0.15)',
              cursor: 'grab',
              userSelect: 'none',
              zIndex: s.layer === 'back' ? 1 : 3,
            }}
          >
            {/* Content: number for shapes, image for assets */}
            {s.type === 'asset' ? (
              <img
                src={s.src}
                alt="asset"
                draggable={false}
                style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none', userSelect: 'none' }}
              />
            ) : (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  color: 'rgba(255,255,255,0.6)',
                  fontWeight: 800,
                  fontSize: Math.max(16, Math.min(s.width, s.height) * 0.45),
                  textShadow: '0 2px 8px rgba(0,0,0,0.25)',
                }}
              >
                {shapeNum}
              </div>
            )}
            {/* Resize handles (only when selected) */}
             {selectedIds.includes(s.id) && (
               <ResizeHandles
                onStart={(e, handle) => {
                  e.stopPropagation();
                  setSelectedIds([s.id]);
                  const rotationRad = (s.rotationDeg * Math.PI) / 180;
                  const rect = workspaceRef.current?.getBoundingClientRect();
                  if (!rect) return;
                  const startMouseX = e.clientX - rect.left;
                  const startMouseY = e.clientY - rect.top;
                  setResizeState({
                    shapeId: s.id,
                    handle,
                    startMouseX,
                    startMouseY,
                    startRect: { x: s.x, y: s.y, width: s.width, height: s.height },
                    rotationRad,
                  });
                }}
              />
            )}
             {selectedIds.includes(s.id) && (
             <button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onDeleteShape(s.id); }}
              title="Hapus"
              style={{
                position: 'absolute',
                top: -10,
                right: -10,
                width: 20,
                height: 20,
                borderRadius: 20,
                background: '#dc3545',
                color: '#fff',
                border: '2px solid #fff',
                boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                cursor: 'pointer',
                lineHeight: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              ×
            </button>
            )}
             {selectedIds.includes(s.id) && (
             <div style={{ position: 'absolute', top: -24, left: '50%', transform: 'translateX(-50%)' }}>
              <div
                onMouseDown={(e) => onStartRotate(e, s.id)}
                title="Putar"
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 16,
                  background: '#0d6efd',
                  border: '2px solid #fff',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                  cursor: 'grab',
                }}
              />
            </div>
            )}
            {/* Radius controls (not for assets) */}
             {s.type !== 'asset' && selectedIds.includes(s.id) && (
                <div style={{ position: 'absolute', bottom: -28, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6 }}>
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); onAdjustRadius(s.id, -4); }}
                  className="start-button"
                  title="Kurangi radius"
                  style={{ padding: '2px 8px', borderRadius: 6, border: '1px solid #ced4da', background: '#fff', cursor: 'pointer' }}
                >
                  −
                </button>
                <div style={{ fontSize: 12, color: '#6c757d', alignSelf: 'center' }}>{Math.round(s.borderRadius ?? 8)}px</div>
                <button
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); onAdjustRadius(s.id, 4); }}
                  className="start-button"
                  title="Tambah radius"
                  style={{ padding: '2px 8px', borderRadius: 6, border: '1px solid #ced4da', background: '#fff', cursor: 'pointer' }}
                >
                  +
                </button>
              </div>
            )}
          </div>
            );
          });
        })()}
      </div>
      <div style={{ marginTop: 8, color: '#6c757d' }}>
        Klik pada area workspace untuk menambahkan shape jika rasio dipilih. Klik kosong untuk membatalkan pilihan shape. Klik shape untuk memilih dan menampilkan kontrol (drag, resize, rotate, delete, radius).
      </div>
    </div>
  );
};

export default Workspace;

function Handle({ position, onMouseDown }) {
  const size = 12;
  const styles = {
    position: 'absolute',
    width: size,
    height: size,
    background: '#0d6efd',
    borderRadius: 4,
    border: '2px solid #fff',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    cursor: `${position}-resize`,
  };
  let posStyle = {};
  if (position === 'nw') posStyle = { left: -size / 2, top: -size / 2 };
  if (position === 'ne') posStyle = { right: -size / 2, top: -size / 2 };
  if (position === 'sw') posStyle = { left: -size / 2, bottom: -size / 2 };
  if (position === 'se') posStyle = { right: -size / 2, bottom: -size / 2 };
  return <div onMouseDown={onMouseDown} style={{ ...styles, ...posStyle }} />;
}

function ResizeHandles({ onStart }) {
  return (
    <>
      <Handle position="nw" onMouseDown={(e) => onStart(e, 'nw')} />
      <Handle position="ne" onMouseDown={(e) => onStart(e, 'ne')} />
      <Handle position="sw" onMouseDown={(e) => onStart(e, 'sw')} />
      <Handle position="se" onMouseDown={(e) => onStart(e, 'se')} />
    </>
  );
}

